import React from 'react';
import { Link } from 'react-router-dom';

export default function Privacy() {
  return (
    <>
      <title>Privacy Policy – ReStaff</title>
      <div className="min-h-screen bg-white">
        <div className="max-w-3xl mx-auto px-6 py-16 md:py-20">

          {/* Logo */}
          <div className="mb-12">
            <Link to="/" className="inline-flex items-center gap-2">
              <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold">RS</span>
              </div>
              <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Manrope' }}>ReStaff</span>
            </Link>
          </div>

          {/* Header */}
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
            Privacy Policy &amp; Privacy Notice
          </h1>
          <p className="text-slate-500 text-sm mb-2">ReStaff – GetReStaff.co</p>
          <p className="text-slate-400 text-sm mb-12">Effective Date: 12 February 2026</p>

          <div className="prose prose-slate max-w-none space-y-10 text-sm text-slate-700 leading-relaxed">

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">1. Introduction</h2>
              <p>This Privacy Policy and Privacy Notice ("Privacy Policy") explains how VoyAsia LLC, operating the ReStaff platform at <a href="https://getrestaff.co" className="text-orange-600 hover:underline">https://getrestaff.co</a> ("ReStaff", "we", "us", "our"), collects, uses, processes, stores, discloses, and safeguards personal data.</p>
              <p className="mt-3">ReStaff is a Software-as-a-Service (SaaS) workforce management platform designed for restaurants and hospitality businesses.</p>
              <p className="mt-3">By accessing our website or using the ReStaff platform, you acknowledge that you have read and understood this Privacy Policy.</p>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">2. Data Controller &amp; Data Processor Roles</h2>
              <p><strong>Company Name:</strong> VoyAsia LLC<br />
              <strong>Jurisdiction:</strong> Wyoming, United States<br />
              <strong>Website:</strong> <a href="https://getrestaff.co" className="text-orange-600 hover:underline">https://getrestaff.co</a><br />
              <strong>Support:</strong> <a href="https://tawk.to/restaff" className="text-orange-600 hover:underline">https://tawk.to/restaff</a></p>

              <h3 className="text-sm font-semibold text-slate-800 mt-4 mb-2">2.1 Website Visitors &amp; Account Holders</h3>
              <p>VoyAsia LLC acts as Data Controller for personal data collected directly from:</p>
              <ul className="list-disc list-inside space-y-1.5 mt-2">
                <li>Website visitors</li>
                <li>Trial users</li>
                <li>Account holders</li>
                <li>Billing contacts</li>
              </ul>

              <h3 className="text-sm font-semibold text-slate-800 mt-4 mb-2">2.2 Employee Data Uploaded by Clients</h3>
              <p>For employee data uploaded or entered by restaurant clients:</p>
              <p className="mt-2">The restaurant client is the Data Controller.<br />
              ReStaff acts strictly as Data Processor, processing such data solely on documented instructions from the client.</p>
              <p className="mt-2">ReStaff does not determine the purpose or legal basis for employee data entered by its clients.</p>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">3. Information We Collect</h2>

              <h3 className="text-sm font-semibold text-slate-800 mt-4 mb-2">3.1 Information You Provide Directly</h3>
              <p>We may collect:</p>
              <ul className="list-disc list-inside space-y-1.5 mt-2">
                <li>Full name</li>
                <li>Business name</li>
                <li>Email address</li>
                <li>Phone number</li>
                <li>Billing information</li>
                <li>Restaurant operational details</li>
                <li>Account login credentials</li>
                <li>Communications submitted via contact forms or support channels</li>
              </ul>

              <h3 className="text-sm font-semibold text-slate-800 mt-4 mb-2">3.2 Employee Data (Uploaded by Clients)</h3>
              <p>Clients may upload or input:</p>
              <ul className="list-disc list-inside space-y-1.5 mt-2">
                <li>Employee names</li>
                <li>Work schedules</li>
                <li>Time clock records (clock-in / clock-out)</li>
                <li>Contact details (if provided by client)</li>
                <li>Role, shift, and attendance data</li>
              </ul>
              <p className="mt-3">This information is processed exclusively under client instruction.</p>

              <h3 className="text-sm font-semibold text-slate-800 mt-4 mb-2">3.3 Automatically Collected Data</h3>
              <p>We may automatically collect:</p>
              <ul className="list-disc list-inside space-y-1.5 mt-2">
                <li>IP address</li>
                <li>Browser type and version</li>
                <li>Device identifiers</li>
                <li>Operating system</li>
                <li>Pages visited</li>
                <li>Date/time of access</li>
                <li>Usage metrics and interaction data</li>
                <li>Cookie identifiers</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">4. Purposes of Processing</h2>
              <p>We process personal data to:</p>
              <ul className="list-disc list-inside space-y-1.5 mt-2">
                <li>Provide, operate, and maintain the ReStaff platform</li>
                <li>Create and manage user accounts</li>
                <li>Process subscription payments</li>
                <li>Deliver customer support</li>
                <li>Improve functionality, performance, and security</li>
                <li>Detect, prevent, and investigate fraud or abuse</li>
                <li>Send administrative or service-related communications</li>
                <li>Comply with legal, regulatory, tax, and accounting obligations</li>
              </ul>
              <p className="mt-3">ReStaff does not sell personal data.</p>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">5. Legal Basis for Processing (GDPR – EEA Users)</h2>
              <p>If located in the EEA, legal bases may include:</p>
              <ul className="list-disc list-inside space-y-1.5 mt-2">
                <li>Contractual necessity</li>
                <li>Legitimate interests</li>
                <li>Legal obligation</li>
                <li>Consent (where required)</li>
              </ul>
              <p className="mt-3">Users may withdraw consent where applicable.</p>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">6. Data Retention</h2>
              <p>We retain personal data:</p>
              <ul className="list-disc list-inside space-y-1.5 mt-2">
                <li>While account remains active</li>
                <li>As necessary to fulfill contractual obligations</li>
                <li>As required for legal, tax, accounting, or regulatory purposes</li>
              </ul>
              <p className="mt-3">Upon account termination:</p>
              <ul className="list-disc list-inside space-y-1.5 mt-2">
                <li>Data may be deleted within 30–90 days</li>
                <li>Backup copies may temporarily persist</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">7. Data Sharing &amp; Subprocessors</h2>
              <p>We may share personal data with trusted providers including:</p>
              <ul className="list-disc list-inside space-y-1.5 mt-2">
                <li>Payment processors (e.g., Stripe)</li>
                <li>Cloud infrastructure providers</li>
                <li>Email and transactional messaging providers</li>
                <li>Analytics providers</li>
                <li>Fraud detection services</li>
                <li>Legal or regulatory authorities when required</li>
              </ul>
              <p className="mt-3">We do not sell or rent personal data.</p>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">8. International Data Transfers</h2>
              <p>ReStaff operates from the United States.</p>
              <p className="mt-3">Personal data may be processed in:</p>
              <ul className="list-disc list-inside space-y-1.5 mt-2">
                <li>The United States</li>
                <li>Other jurisdictions where infrastructure providers operate</li>
              </ul>
              <p className="mt-3">Where required, appropriate safeguards may be implemented.</p>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">9. Security Measures</h2>
              <p>We implement commercially reasonable safeguards including:</p>
              <ul className="list-disc list-inside space-y-1.5 mt-2">
                <li>Encrypted HTTPS (SSL/TLS)</li>
                <li>Secure hosting environments</li>
                <li>Role-based access controls</li>
                <li>Logical data segregation</li>
                <li>Password-protected accounts</li>
                <li>Restricted internal access</li>
                <li>Monitoring for suspicious activity</li>
              </ul>
              <p className="mt-3">No system guarantees absolute security.</p>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">10. Your Privacy Rights</h2>
              <p>Depending on jurisdiction, you may have rights to:</p>
              <ul className="list-disc list-inside space-y-1.5 mt-2">
                <li>Access</li>
                <li>Correction</li>
                <li>Deletion</li>
                <li>Restriction</li>
                <li>Objection</li>
                <li>Data portability</li>
                <li>Withdrawal of consent</li>
              </ul>
              <p className="mt-3">To exercise rights: <a href="https://tawk.to/restaff" className="text-orange-600 hover:underline">https://tawk.to/restaff</a></p>
              <p className="mt-2">Identity verification may be required.</p>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">11. California Privacy Rights (CCPA / CPRA)</h2>
              <p>California residents may request:</p>
              <ul className="list-disc list-inside space-y-1.5 mt-2">
                <li>Categories of personal information collected</li>
                <li>Deletion of personal information</li>
                <li>Disclosure of sharing practices</li>
              </ul>
              <p className="mt-3">ReStaff does not sell personal information.</p>
              <p className="mt-2">Requests via: <a href="https://tawk.to/restaff" className="text-orange-600 hover:underline">https://tawk.to/restaff</a></p>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">12. Canadian Privacy Rights (PIPEDA)</h2>
              <p>For Canadian users, data is handled in accordance with PIPEDA.</p>
              <p className="mt-3">Individuals may request access or correction.</p>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">13. Cookies &amp; Tracking Technologies</h2>
              <p>We use cookies for:</p>
              <ul className="list-disc list-inside space-y-1.5 mt-2">
                <li>Essential functionality</li>
                <li>Security</li>
                <li>Performance monitoring</li>
                <li>Analytics</li>
              </ul>
              <p className="mt-3">Users may manage preferences via browser settings.</p>
              <p className="mt-2">Where required, cookie consent banners are displayed.</p>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">14. Children's Privacy</h2>
              <p>ReStaff is not intended for individuals under 18.</p>
              <p className="mt-3">We do not knowingly collect data from minors.</p>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">15. Data Processing Agreement (DPA)</h2>
              <p>Business clients may request a separate DPA.</p>
              <p className="mt-3">Restaurant clients must:</p>
              <ul className="list-disc list-inside space-y-1.5 mt-2">
                <li>Have authority to upload employee data</li>
                <li>Provide required privacy notices</li>
                <li>Obtain necessary consents</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">16. Account Deletion</h2>
              <p>Users may request deletion via support.</p>
              <p className="mt-3">Upon deletion:</p>
              <ul className="list-disc list-inside space-y-1.5 mt-2">
                <li>Access is terminated</li>
                <li>Data removed subject to retention obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">17. Policy Updates</h2>
              <p>We may modify this Privacy Policy at any time.</p>
              <p className="mt-3">Updated versions will include revised effective date.</p>
              <p className="mt-2">Continued use constitutes acceptance.</p>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">18. Contact Information</h2>
              <p><strong>Privacy Inquiries:</strong><br />
              <a href="https://tawk.to/restaff" className="text-orange-600 hover:underline">https://tawk.to/restaff</a></p>
              <p className="mt-3"><strong>Website:</strong><br />
              <a href="https://getrestaff.co" className="text-orange-600 hover:underline">https://getrestaff.co</a></p>
              <p className="mt-3"><strong>Company:</strong><br />
              VoyAsia LLC<br />Wyoming, United States</p>
            </section>

          </div>

          {/* Back link */}
          <div className="mt-16 pt-8 border-t border-slate-100">
            <a href="/" className="text-sm text-orange-600 hover:underline">← Back to ReStaff</a>
          </div>

        </div>
      </div>
    </>
  );
}
